import { enableField, disableField } from '../../../actions';
import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Mp1SensorIds, Mp1SensorTitles, Mp2SensorIds, Mp2SensorTitles, TritonSensors, getSensorFieldId } from '../base-config-models';
import { SurfaceComboSensorFieldIds } from './config-models';
import { MonitoringPointElement } from 'app/shared/components/location-card/models/location-card.model';
import { LocationCardSettings } from '../../../reducers';
import { field } from '../../../core/field-schema';
import { required, pattern, min, max } from '../../../core/validator-builders';
import { SurfaceComboDevice } from '../models';
import {
    MaxVelocityMetricValuesForParaFlow,
    MaxVelocityNonMetricValuesForParaFlow,
    MaxVelocityMetricValuesNonParaFlow,
    MaxVelocityNonMetricValuesNonParaFlow,
} from 'app/shared/components/location-card/location-card.constants';

/**
 * Surface Combo sensor configuration
 */
export const surfaceComboSensor = (mp: 0 | 1, settings: LocationCardSettings, sensorData: SurfaceComboDevice): Section => {
    const isMetric = settings.isMetric;
    const surfaceMaxVelocityOptions = isMetric ? MaxVelocityMetricValuesForParaFlow : MaxVelocityNonMetricValuesForParaFlow;
    const maxVelocityOptions = isMetric ? MaxVelocityMetricValuesNonParaFlow : MaxVelocityNonMetricValuesNonParaFlow;

    return {
        info: {
            id: mp === 0 ? Mp1SensorIds[TritonSensors.SurfaceCombo] : Mp2SensorIds[TritonSensors.SurfaceCombo],
            title: mp === 0 ? Mp1SensorTitles[TritonSensors.SurfaceCombo] : Mp2SensorTitles[TritonSensors.SurfaceCombo],
            expandable: true,
            displayTitle: true,
            expanded: false,
        },
        rows: [
            // Physical Offset Row
            {
                info: {
                    id: 'physical-offset-row',
                    panelClass: 'form-input-bottom-space',
                },
                items: [
                    field.number(getSensorFieldId(SurfaceComboSensorFieldIds.PhysicalOffset, mp), 'physicalOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_PHYSICAL',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.physicalOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'full-width required',
                    }),
                ],
            },

            // Ultrasonic Settings Row
            {
                info: {
                    id: 'ultrasonic-settings-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.number(getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicElectronicOffset, mp), 'ultrasonicElectronicOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ELECTRONIC',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.ultrasonicElectronicOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item width-30 required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicMode, mp), 'ultrasonicMode', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MODE',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        value: sensorData ? sensorData.ultrasonicMode : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.ModeList,
                        panelClass: 'each-Item width-30 required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicBlanking, mp), 'ultrasonicBlanking', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_BLANKING',
                        value: sensorData ? sensorData.ultrasonicBlanking : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.BlankingList,
                        panelClass: 'each-Item width-30 required',
                    }),
                ],
            },

            // Ultrasonic Toggles Row
            {
                info: {
                    id: 'ultrasonic-toggles-row',
                    panelClass: 'custom-50p pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicDisableSurchargeDetection, mp), 'ultrasonicDisableSurchargeDetection', {
                        value: sensorData ? sensorData.ultrasonicDisableSurchargeDetection : true,
                        label: 'ADD_EDIT.SURCHARGE_DETECTION',
                        panelClass: 'each-Item item-flex-center',
                    }),
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.UltrasonicStoreData, mp), 'ultrasonicStoreData', {
                        value: sensorData ? sensorData.ultrasonicStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'each-Item item-flex-center',
                    }),
                ],
            },

            // Surface Velocity Toggle Row
            {
                info: {
                    id: 'surface-velocity-toggle-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityEnable, mp), 'surfaceVelocityEnable', {
                        value: sensorData ? sensorData.surfaceVelocityEnable : true,
                        label: 'ADD_EDIT.SURFACE_VELOCITY',
                        panelClass: 'font-600 item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityFrequency, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityFrequency, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocitySensitivity, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocitySensitivity, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDirection, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDirection, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceComboSVmaxVelocity, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceComboSVmaxVelocity, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityMaximum, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityMaximum, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityGain, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityGain, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDuration, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDuration, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityScrubbing, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityScrubbing, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityStoreData, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Surface Velocity Settings Row 1
            {
                info: {
                    id: 'surface-velocity-settings-1-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(
                        getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityFrequency, mp),
                        'surfaceVelocityFrequency',
                        {
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            placeholder: 'ADD_EDIT.TRANSMIT_FREQUENCY',
                            disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                            value: sensorData ? sensorData.surfaceVelocityFrequency : 0,
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.SVTransmitFrequencyList,
                            panelClass: 'each-Item width-30 required',
                        },
                    ),
                    field.select<MonitoringPointElement, number>(
                        getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocitySensitivity, mp),
                        'surfaceVelocitySensitivity',
                        {
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSITIVITY',
                            disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                            value: sensorData ? sensorData.surfaceVelocitySensitivity : 12,
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.SensitivityList,
                            panelClass: 'each-Item width-30 required',
                        },
                    ),
                    field.select<MonitoringPointElement, number>(
                        getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDirection, mp),
                        'surfaceVelocityDirection',
                        {
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DIRECTION',
                            disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                            value: sensorData ? sensorData.surfaceVelocityDirection : 1,
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.SVDirectionList,
                            panelClass: 'each-Item width-30 required',
                        },
                    ),
                ],
            },

            // Surface Velocity Settings Row 2
            {
                info: {
                    id: 'surface-velocity-settings-2-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(
                        getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityMaximum, mp),
                        'surfaceVelocityMaximum',
                        {
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_VALUE_MAXIMUM',
                            disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                            value: sensorData && surfaceMaxVelocityOptions.find((v) => v.value === sensorData.surfaceVelocityMaximum)
                                ? sensorData.surfaceVelocityMaximum
                                : 0,
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.SurfaceMaxVelocity,
                            panelClass: 'each-Item full-width required',
                        },
                    ),
                    field.number(getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityGain, mp), 'surfaceVelocityGain', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MONITORGAIN',
                        value: sensorData ? sensorData.surfaceVelocityGain : 0.9,
                        disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                        validators: [
                            required(),
                            min({ value: 0 }),
                            max({ value: 2 }),
                            pattern({
                                value: REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: 'CUSTOMER_EDITOR.RAIN_PROFILE.STORM_EVENT.INVALID_INPUT_TXT',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                ],
            },

            // Surface Velocity Settings Row 3
            {
                info: {
                    id: 'surface-velocity-settings-3-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(
                        getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityDuration, mp),
                        'surfaceVelocityDuration',
                        {
                            valueMapper: (option) => option.value,
                            displayMapper: (option) => option.text,
                            disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DURATION',
                            value: sensorData ? sensorData.surfaceVelocityDuration : 3,
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.SVDurationList,
                            panelClass: 'each-Item full-width required',
                        },
                    ),
                    field.checkbox(getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityScrubbing, mp), 'surfaceVelocityScrubbing', {
                        disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                        value: sensorData ? sensorData.surfaceVelocityScrubbing : true,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SCRUBBLING',
                        panelClass: 'each-Item item-flex-center',
                    }),
                ],
            },

            // Surface Velocity Store Data Row
            {
                info: {
                    id: 'surface-velocity-store-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceVelocityStoreData, mp), 'surfaceVelocityStoreData', {
                        disabled: sensorData ? !sensorData.surfaceVelocityEnable : false,
                        value: sensorData ? sensorData.surfaceVelocityStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'item-flex-center',
                    }),
                ],
            },

            // Peak Velocity Toggle Row
            {
                info: {
                    id: 'peak-velocity-toggle-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.VelocityEnable, mp), 'velocityEnable', {
                        value: sensorData ? sensorData.velocityEnable : true,
                        label: 'ADD_EDIT.PEAK_VELOCITY',
                        panelClass: 'font-600 item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityFrequency, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityFrequency, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocitySensitivity, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocitySensitivity, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityDirection, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityDirection, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceComboPVmaxVelocity, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.SurfaceComboPVmaxVelocity, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityMaximum, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityMaximum, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityGain, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityGain, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityStoreData, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityScrubbing, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.VelocityScrubbing, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Peak Velocity Settings Row 1
            {
                info: {
                    id: 'peak-velocity-settings-1-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SurfaceComboSensorFieldIds.VelocityFrequency, mp), 'velocityFrequency', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.TRANSMIT_FREQUENCY',
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityFrequency : 1,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.TransmitFrequencyList,
                        panelClass: 'each-Item width-30 required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SurfaceComboSensorFieldIds.VelocitySensitivity, mp), 'velocitySensitivity', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSITIVITY',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocitySensitivity : 10,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.SensitivityList,
                        panelClass: 'each-Item width-30 required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SurfaceComboSensorFieldIds.VelocityDirection, mp), 'velocityDirection', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DIRECTION',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityDirection : 1,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.PVDirectionList,
                        panelClass: 'each-Item width-30 required',
                    }),
                ],
            },

            // Peak Velocity Settings Row 2
            {
                info: {
                    id: 'peak-velocity-settings-2-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SurfaceComboSensorFieldIds.VelocityMaximum, mp), 'velocityMaximum', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_VALUE_MAXIMUM',
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData && maxVelocityOptions.find((v) => v.value === sensorData.velocityMaximum)
                            ? sensorData.velocityMaximum
                            : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MaxVelocity,
                        panelClass: 'each-Item full-width required',
                    }),
                    field.number(getSensorFieldId(SurfaceComboSensorFieldIds.VelocityGain, mp), 'velocityGain', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MONITORGAIN',
                        value: sensorData ? sensorData.velocityGain : 0.9,
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        validators: [
                            required(),
                            min({ value: 0 }),
                            max({ value: 2 }),
                            pattern({
                                value: REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: 'CUSTOMER_EDITOR.RAIN_PROFILE.STORM_EVENT.INVALID_INPUT_TXT',
                            }),
                        ],
                        panelClass: 'each-Item required',
                    }),
                ],
            },

            // Peak Velocity Store & Scrubbing Row
            {
                info: {
                    id: 'peak-velocity-store-scrub-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.VelocityStoreData, mp), 'velocityStoreData', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'each-Item item-flex-center',
                    }),
                    field.checkbox(getSensorFieldId(SurfaceComboSensorFieldIds.VelocityScrubbing, mp), 'velocityScrubbing', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityScrubbing : true,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SCRUBBLING',
                        panelClass: 'each-Item item-flex-center',
                    }),
                ],
            },

            // Info Text Row
            {
                info: {
                    id: 'info-text-row',
                    panelClass: 'mt-15',
                },
                items: [
                    {
                        id: 'info-text',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'infoText',
                        label: 'ADD_EDIT.ADD_EDIT_TEXT',
                        panelClass: 'toggle input-text font-600',
                    },
                ],
            },

            // Temperature & Sensor Row
            {
                info: {
                    id: 'temperature-sensor-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.TemperatureStoreData, mp), 'temperatureStoreData', {
                        value: sensorData ? sensorData.temperatureStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'each-Item item-flex-center',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SurfaceComboSensorFieldIds.Sensor, mp), 'sensor', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSOR',
                        value: sensorData ? sensorData.temperatureSensor : 1,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.TemperatureSensors,
                        panelClass: 'each-Item full-width required item-flex-center',
                    }),
                ],
            },

            // Pressure Toggle Row
            {
                info: {
                    id: 'pressure-toggle-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.PressureEnable, mp), 'pressureEnable', {
                        value: sensorData ? sensorData.pressureEnable : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_PRESSURE',
                        panelClass: 'font-600 item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.PressureElectronicOffset, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.PressureElectronicOffset, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.PressureCalibration, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.PressureCalibration, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.PressureStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(SurfaceComboSensorFieldIds.PressureStoreData, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Pressure Settings Row
            {
                info: {
                    id: 'pressure-settings-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.number(getSensorFieldId(SurfaceComboSensorFieldIds.PressureElectronicOffset, mp), 'pressureElectronicOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ELECTRONIC',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.pressureElectronicOffset : 0,
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.numericDecimal1PlacePattern
                                    : REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item width-30 required',
                    }),
                    field.checkbox(getSensorFieldId(SurfaceComboSensorFieldIds.PressureCalibration, mp), 'pressureCalibration', {
                        value: sensorData ? sensorData.pressureCalibration : true,
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        placeholder: 'ADD_EDIT.AUTOMATIC_CALIBRATION',
                        panelClass: 'each-Item width-30 item-flex-center',
                    }),
                    field.toggle(getSensorFieldId(SurfaceComboSensorFieldIds.PressureStoreData, mp), 'pressureStoreData', {
                        value: sensorData ? sensorData.pressureStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        panelClass: 'each-Item item-flex-center width-30 custom-slide-toggle',
                    }),
                ],
            },
        ],
    };
};
